import { useEffect, useState } from "react";
import { useFarm } from "../context/FarmContext";
import { api } from "../lib/api";
import { livestockById } from "../lib/constants";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Sparkles, AlertTriangle, Info, ShieldAlert, Lightbulb, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const LEVEL = {
  danger: { icon: ShieldAlert, cls: "text-destructive bg-destructive/10 border-destructive/30" },
  warning: { icon: AlertTriangle, cls: "text-[hsl(var(--terracotta))] bg-[hsl(var(--terracotta)/0.1)] border-[hsl(var(--terracotta)/0.3)]" },
  info: { icon: Info, cls: "text-primary bg-primary/8 border-primary/20" },
};
const IMPACT = { High: "bg-destructive text-destructive-foreground", Medium: "bg-accent text-accent-foreground", Low: "bg-secondary text-secondary-foreground" };

export default function Insights() {
  const { settings } = useFarm();
  const [rules, setRules] = useState([]);
  const [ai, setAi] = useState([]);
  const [aiRaw, setAiRaw] = useState("");
  const [loading, setLoading] = useState(false);
  const active = livestockById(settings.active_livestock);

  useEffect(() => { api.ruleSuggestions().then((d) => setRules(d.tips)); }, []);

  const runAI = async () => {
    setLoading(true);
    setAiRaw("");
    try {
      const d = await api.aiSuggestions(settings.active_livestock);
      setAi(d.suggestions || []);
      if (d.raw) setAiRaw(d.raw);
      toast.success("AI suggestions generated");
    } catch (e) {
      toast.error("AI generation failed. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-8">
      <div>
        <h1 className="font-display text-3xl font-extrabold tracking-tight">Insights &amp; Suggestions</h1>
        <p className="text-sm text-muted-foreground mt-1">Rule-based tips plus AI-powered recommendations for your {active.name.toLowerCase()} operation.</p>
      </div>

      <section>
        <div className="flex items-center gap-2 mb-4">
          <Sparkles className="h-5 w-5 text-accent" />
          <h2 className="font-display text-xl font-bold tracking-tight">AI Smart Optimizer</h2>
        </div>
        <Card className="p-6" data-testid="ai-suggestions-container">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-muted-foreground max-w-md">
              Analyzes your live financials and {active.name.toLowerCase()} profile to generate tailored improvement advice.
            </p>
            <Button onClick={runAI} disabled={loading} className="rounded-full gap-1.5" data-testid="generate-ai-button">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : ai.length ? <RefreshCw className="h-4 w-4" /> : <Sparkles className="h-4 w-4" />}
              {loading ? "Analyzing…" : ai.length ? "Regenerate" : "Generate AI Suggestions"}
            </Button>
          </div>

          {ai.length > 0 && (
            <div className="mt-6 grid sm:grid-cols-2 gap-4">
              {ai.map((s, i) => (
                <div key={i} className="rounded-xl border border-border p-5 animate-fade-up" style={{ animationDelay: `${i * 70}ms` }} data-testid={`ai-suggestion-${i}`}>
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="font-display font-bold">{s.title}</h3>
                    <Badge className={`${IMPACT[s.impact] || IMPACT.Medium} border-0 shrink-0`}>{s.impact}</Badge>
                  </div>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{s.advice}</p>
                </div>
              ))}
            </div>
          )}
          {aiRaw && <pre className="mt-4 whitespace-pre-wrap rounded-lg bg-secondary p-4 text-xs">{aiRaw}</pre>}
          {!ai.length && !loading && !aiRaw && (
            <div className="mt-6 grid place-items-center py-8 text-center">
              <Sparkles className="h-8 w-8 text-muted-foreground/40" />
              <p className="mt-2 text-sm text-muted-foreground">Click generate to get AI recommendations.</p>
            </div>
          )}
        </Card>
      </section>

      <section>
        <div className="flex items-center gap-2 mb-4">
          <Lightbulb className="h-5 w-5 text-accent" />
          <h2 className="font-display text-xl font-bold tracking-tight">Rule-Based Tips</h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="rule-suggestions-container">
          {rules.map((t, i) => {
            const cfg = LEVEL[t.level] || LEVEL.info;
            const Icon = cfg.icon;
            return (
              <Card key={i} className={`p-5 border ${cfg.cls} animate-fade-up`} style={{ animationDelay: `${i * 60}ms` }} data-testid={`rule-tip-${i}`}>
                <div className="flex items-center gap-2"><Icon className="h-4.5 w-4.5" /><h3 className="font-semibold">{t.title}</h3></div>
                <p className="mt-2 text-sm leading-relaxed opacity-90">{t.message}</p>
              </Card>
            );
          })}
        </div>
      </section>
    </div>
  );
}
